'use client';

import React from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  resolvePerformanceLabBuyCategory,
} from 'components/performance-lab/utils';
import { useHandlePerformanceLabBuyNavigate } from 'components/performance-lab/hooks';

import { PlanIncludeDetail } from './types';
import { CartAdd, CartDecrease } from 'assets/icons';
import {
  SharedDetailModal,
  SharedDetailModalSection,
} from 'components/performance-lab/SharedDetailModal';
import styles from './IncludeDetailSidebar.module.css';

interface IncludeDetailSidebarProps {
  label: string;
  detail: PlanIncludeDetail;
  onClose: () => void;
  onAdd: () => void;
  iconUrl?: string;
  cartLine: any | null;
  quantityInCart: number;
  onIncrement: () => void;
  onDecrement: () => void;
  cartLoading: boolean;
  isComingSoon?: boolean;
  isCartEnabled?: boolean;
  planId?: string;
  sectionId?: string;
  shopifyVariantId?: string;
}

const IncludeDetailSidebar: React.FC<IncludeDetailSidebarProps> = ({
  label,
  detail,
  onClose,
  onAdd,
  iconUrl,
  cartLine,
  quantityInCart,
  onIncrement,
  onDecrement,
  cartLoading,
  isComingSoon,
  isCartEnabled,
  planId,
  sectionId,
  shopifyVariantId,
}) => {
  const t = useTranslations('diagnostics');
  const tLab = useTranslations('performance-lab');
  const searchParams = useSearchParams();
  const handleBuyNavigate = useHandlePerformanceLabBuyNavigate();
  const internationalBookLabel = tLab('cta.buyNow');
  const resolveText = (value: string) => t(value);
  const modalSections: SharedDetailModalSection[] | undefined = detail.sections?.map(
    (section) => ({
      title: section.title,
      content: section.content,
    }),
  );
  const sectionImageSrc = detail.imageUrl ?? detail.thumbnailUrl;
  const closeAriaLabel = t('modal.closeAria');

  return (
    <SharedDetailModal
      title={label}
      sections={modalSections}
      description={detail.description}
      benefits={detail.benefits}
      iconSrc={iconUrl}
      iconAlt={t(label)}
      showIconPlaceholder
      sectionImageSrc={sectionImageSrc}
      onClose={onClose}
      closeAriaLabel={closeAriaLabel}
      resolveText={resolveText}
      footer={
        isComingSoon ? (
          <button className={styles.footerButton} type="button" disabled>
            {t('ui.comingSoon')}
          </button>
        ) : !isCartEnabled ? (
          <button
            className={`${styles.footerButton} gtm`}
            data-buttontype="performance_lab_buy_sidebar_book"
            data-testid={`pl-sidebar-book-${planId}`}
            data-product={sectionId}
            data-outlined="true"
            type="button"
            onClick={() => {
              onClose();
              const rawCategory = searchParams?.get('category') ?? undefined;
              const category = resolvePerformanceLabBuyCategory(
                rawCategory,
                sectionId,
              );

              handleBuyNavigate({ planId, category });
            }}
          >
            {internationalBookLabel}
          </button>
        ) : cartLine ? (
          <div className={styles.footerQuantityWrapper}>
            <button
              className={`${styles.footerQuantityButton} gtm`}
              data-buttontype="performance_lab_buy_sidebar_qty_decrement"
              data-testid={`pl-sidebar-qty-dec-${planId}`}
              data-product={sectionId}
              data-variantid={shopifyVariantId}
              onClick={onDecrement}
              disabled={cartLoading}
            >
              <CartDecrease fill="#1539f5" />
            </button>
            <span className={styles.footerQuantityValue}>{quantityInCart}</span>
            <button
              className={`${styles.footerQuantityButton} gtm`}
              data-buttontype="performance_lab_buy_sidebar_qty_increment"
              data-testid={`pl-sidebar-qty-inc-${planId}`}
              data-product={sectionId}
              data-variantid={shopifyVariantId}
              onClick={onIncrement}
              disabled={cartLoading}
            >
              <CartAdd fill="#1539f5" />
            </button>
          </div>
        ) : (
          <button
            className={`${styles.footerButton} gtm`}
            data-buttontype="performance_lab_buy_include_section_addtocart"
            data-testid={`pl-sidebar-add-${planId}`}
            data-product={sectionId}
            data-variantid={shopifyVariantId}
            onClick={onAdd}
            type="button"
          >
            {t('ui.add')}
          </button>
        )
      }
    />
  );
};

export default IncludeDetailSidebar;
